import type { LensScope } from '~/types/leLens';

export type ScopeRowId = 'dataset' | 'metric_basis' | 'period' | 'geography' | 'attribution';

export const SCOPE_ROWS: ReadonlyArray<{ id: ScopeRowId; label: string }> = [
  { id: 'dataset', label: 'Dataset' },
  { id: 'metric_basis', label: 'Metric basis' },
  { id: 'period', label: 'Period' },
  { id: 'geography', label: 'Region means' },
  { id: 'attribution', label: 'Attribution' },
];

/** True when `comparison.label` already opens with its own "vs" (svc-reporting's usual
 *  convention, e.g. "vs the same days last month"). Some labels do not (e.g. "Previous 30
 *  completed Sydney days" alongside a period label that is only a window's own name, not a
 *  continuation sentence), and those need the joiner supplied here instead of doubling up. */
function comparisonLabelStartsWithVs(label: string): boolean {
  return /^vs\b/i.test(label.trim());
}

/** The row's resolved value in plain English, or null when the planner has nothing to state for
 *  it. That is not the same as unresolved: a brief with no geography words at all still has a
 *  null geography and no clarification for it.
 *
 *  `datasetNameFallback` fills the dataset row when `scope.dataset` is itself null but the plan
 *  or the saved spec binds exactly one dataset regardless (a routed plan's own `scope.dataset` is
 *  null until every card is bound to a single answer, and a saved Lens with one dataset is not
 *  the same fact as the planner having named one, but it is a fact this row should still state
 *  rather than reading "Not set" over a board that plainly reads from one place). */
export function scopeRowValue(
  id: ScopeRowId,
  scope: LensScope | undefined,
  datasetNameFallback?: string,
): string | null {
  if (!scope) {
    return null;
  }
  switch (id) {
    case 'dataset':
      return scope.dataset?.name ?? datasetNameFallback ?? null;
    case 'metric_basis':
      return scope.metricBasis?.label ?? null;
    case 'period':
      if (!scope.period) {
        return null;
      }
      if (!scope.comparison) {
        return scope.period.label;
      }
      return comparisonLabelStartsWithVs(scope.comparison.label)
        ? `${scope.period.label} ${scope.comparison.label}`
        : `${scope.period.label} vs ${scope.comparison.label}`;
    case 'geography':
      return scope.geography?.label ?? null;
    case 'attribution':
      if (!scope.attribution) {
        return null;
      }
      return 'notApplicable' in scope.attribution ? 'Not applicable' : scope.attribution.label;
    default:
      return null;
  }
}

/** One segment of the canvas scope line, in the same words the confirmation block itself uses.
 *  `geographyFilterValue` is the *current* value of the filter `scopeGeographyFilter` minted for
 *  this field (empty until the reader picks one): the scope itself only ever states what "region"
 *  *means*, never which one is theirs, so the segment has to carry the filter's own live value to
 *  say anything more specific than that. */
function formatScopeSegment(
  id: ScopeRowId,
  scope: LensScope,
  geographyFilterValue?: string | Array<string>,
  datasetNameFallback?: string,
): string | null {
  const value = scopeRowValue(id, scope, datasetNameFallback);
  if (!value) {
    return null;
  }
  if (id === 'geography') {
    const chosen = Array.isArray(geographyFilterValue) ? geographyFilterValue.join(', ') : geographyFilterValue;
    return chosen ? `Region = ${value}: ${chosen}` : `Region = ${value} · choose yours in the filter`;
  }
  if (id === 'attribution') {
    return value === 'Not applicable' ? 'Attribution not applicable' : `Attribution: ${value}`;
  }
  return value;
}

/**
 * The one line LeLensPage shows under the Lens title once it is saved: "Sales · TTV, before
 * refunds · 1 to 23 Sep vs 1 to 23 Aug · Region = destination region: Bali · Attribution not
 * applicable" (journey_visual.png; the geography segment reads "· choose yours in the filter"
 * until `geographyFilterValue` names one). Null when the Lens carries no scope at all, e.g. one
 * saved before this existed; a term the planner had nothing to say about is simply left out
 * rather than shown as a blank segment.
 */
export function formatScopeLine(
  scope: LensScope | undefined,
  geographyFilterValue?: string | Array<string>,
  datasetNameFallback?: string,
  options: {
    includeDataset?: boolean;
    includeMetricBasis?: boolean;
    includeNotApplicableAttribution?: boolean;
  } = {},
): string | null {
  if (!scope) {
    return null;
  }
  const segments = SCOPE_ROWS.filter(({ id }) => {
    if (id === 'dataset' && options.includeDataset === false) return false;
    if (id === 'metric_basis' && options.includeMetricBasis === false) return false;
    if (
      id === 'attribution' &&
      options.includeNotApplicableAttribution === false &&
      scope.attribution &&
      'notApplicable' in scope.attribution
    ) {
      return false;
    }
    return true;
  })
    .map(({ id }) => formatScopeSegment(id, scope, geographyFilterValue, datasetNameFallback))
    .filter((segment): segment is string => Boolean(segment));
  return segments.length ? segments.join(' · ') : null;
}
